import React, { useState } from 'react';
import { FaKeyboard, FaLanguage, FaRegCopy, FaChevronDown } from 'react-icons/fa6';
import { allLanguages, getFlagUrl } from '../utils/languages';
import { convertAmount, formatCurrency } from '../utils/converter';
import { convertToEnglish } from '../utils/converters/english';

const Converter = ({ amount, setAmount, selectedLang, setSelectedLang, onCopy }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const currentLangObj = allLanguages.find(l => l.code === selectedLang) || allLanguages[0];
  const convertedText = convertAmount(amount, selectedLang);
  const formattedNumber = formatCurrency(amount);
  const englishText = convertToEnglish(parseFloat(amount) || 0, false);

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setAmount(val);
    }
  };

  // Improved Dark Mode Contrast Styles
  const boxStyle = "bg-white dark:bg-neutral-800 rounded-lg p-5 min-h-[80px] text-lg md:text-xl break-words border border-gray-200 dark:border-neutral-700 shadow-sm dark:shadow-neutral-950/50 flex items-center transition-colors duration-300";

  return (
    <div className="glass-card rounded-xl shadow-lg overflow-hidden animate-fade-in mb-8">
      <div className="h-1 w-full bg-gradient-to-r from-primary-400 via-secondary-400 to-accent-400"></div>
      
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-5 flex items-center">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
              <FaKeyboard />
            </span>
            輸入金額
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">金額</label>
              <input 
                type="text" 
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                placeholder="輸入數字 (例如: 1234.56)"
                className="w-full rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-gray-700 dark:text-gray-100 px-4 py-3.5 text-base shadow-sm focus-ring transition-colors"
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">轉換語言</label>
              <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-gray-700 dark:text-gray-100 px-4 py-3.5 text-base shadow-sm flex items-center justify-between focus-ring"
              >
                <div className="flex items-center">
                  <img src={getFlagUrl(currentLangObj.flag)} alt="flag" className="w-6 h-4 object-cover rounded-sm mr-2.5 shadow-sm" />
                  <span>{currentLangObj.name}</span>
                </div>
                <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && (
                <div className="absolute z-20 w-full mt-1 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 shadow-xl max-h-60 overflow-y-auto">
                  {allLanguages.map(lang => (
                    <div 
                      key={lang.code}
                      onClick={() => { setSelectedLang(lang.code); setIsOpen(false); }}
                      className="flex items-center px-4 py-3 hover:bg-gray-100 dark:hover:bg-neutral-700 cursor-pointer text-gray-700 dark:text-gray-200"
                    >
                      <img src={getFlagUrl(lang.flag)} alt={lang.flag} className="w-6 h-4 object-cover rounded-sm mr-3 shadow-sm" />
                      <span className={`text-sm ${lang.font === 'korean' ? 'font-korean' : ''}`}>
                        {lang.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-neutral-700 my-6"></div>

        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-5 flex items-center">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400 mr-3">
              <FaLanguage />
            </span>
            轉換結果
          </h2>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">數字大寫格式</label>
              <button 
                onClick={() => onCopy(convertedText)} 
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                disabled={convertedText === '等待轉換...'}
              >
                <FaRegCopy /> 複製
              </button>
            </div>
            <div className={`${boxStyle} ${currentLangObj.rtl ? 'flex-row-reverse text-right' : 'text-left'} ${currentLangObj.font === 'korean' ? 'font-korean' : ''} text-gray-900 dark:text-zinc-50 font-semibold`}>
              <span className="w-full">{convertedText}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">格式化數字</label>
                <button onClick={() => onCopy(formattedNumber)} className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                  <FaRegCopy /> 複製
                </button>
              </div>
              <div className="bg-gray-50 dark:bg-neutral-800/80 rounded-lg p-4 font-mono text-base border border-gray-200 dark:border-neutral-700 text-gray-800 dark:text-zinc-100 shadow-inner">
                {formattedNumber}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300">英文大寫 (參考)</label>
                <button onClick={() => onCopy(englishText)} className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                  <FaRegCopy /> 複製
                </button>
              </div>
              <div className="bg-gray-50 dark:bg-neutral-800/80 rounded-lg p-4 text-base border border-gray-200 dark:border-neutral-700 text-gray-800 dark:text-zinc-100 shadow-inner">
                {englishText}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Converter;